import type { PivotDateGrouping } from "./types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad = (n: number) => String(n).padStart(2, "0");

const isoWeek = (date: Date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return { year: d.getFullYear(), week };
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  // "2025-01-01" is a calendar day; `new Date` would read it as UTC midnight and shift it a day west of UTC
  const dateOnly = typeof value === "string" ? DATE_ONLY.exec(value) : null;
  const date =
    value instanceof Date
      ? value
      : dateOnly
        ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3])
        : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const dateBucket = (
  value: unknown,
  grouping: PivotDateGrouping,
): { key: string; label: string } | null => {
  const date = toDate(value);
  if (!date) return null;
  const y = date.getFullYear();
  const m = date.getMonth();
  switch (grouping) {
    case "year":
      return { key: String(y), label: String(y) };
    case "quarter": {
      const q = Math.floor(m / 3) + 1;
      return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
    }
    case "month":
      return { key: `${y}-${pad(m + 1)}`, label: `${MONTHS[m]} ${y}` };
    case "week": {
      const { year, week } = isoWeek(date);
      return { key: `${year}-W${pad(week)}`, label: `Week ${week}, ${year}` };
    }
    case "day":
      return {
        key: `${y}-${pad(m + 1)}-${pad(date.getDate())}`,
        label: `${pad(m + 1)}/${pad(date.getDate())}/${y}`,
      };
  }
};

/** Bucket keys start with the year, so "same period last year" swaps it */
export const priorYearKey = (key: string) => {
  const year = Number(key.slice(0, 4));
  return Number.isFinite(year) ? `${year - 1}${key.slice(4)}` : null;
};

export const yearOfKey = (key: string) => key.slice(0, 4);
